import { useEffect, useState } from 'react';
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

import { SortableTableContents, TextButtonWithIcon } from '@/components';
import {
  getLanguageVariantByDefaultPriority,
  HandleQuizAnswerData,
  onQueryError,
  onQuerySuccess,
  Quiz,
  QuizAnswer,
  useProduct,
} from '@/core';
import { DialogHandleVariant, VariantQuizAnswer } from '@/features';
import palette from '@/theme/palette.ts';

type EditableVariantProps = {
  quiz: Quiz;
};

export const EditableVariant = ({ quiz }: EditableVariantProps) => {
  const [answers, setAnswers] = useState<QuizAnswer[]>(quiz.answers);
  const [addVariantDialogOpen, setAddVariantDialogOpen] = useState(false);
  const {
    mutations: { createProductQuizAnswerMutation },
    queries: { useProductQuizzes },
  } = useProduct();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { data: quizResponse } = useProductQuizzes(Number(id));

  useEffect(() => {
    if (quizResponse && quizResponse.status === 200 && quizResponse.data) {
      setAnswers(
        quizResponse.data
          .find((r) => r.id === quiz.id)
          ?.answers.sort((a, b) => (a?.ordering ?? 0) - (b?.ordering ?? 0)) || [],
      );
    }
  }, [quiz.id, quizResponse]);

  const isMultipleChoice = quiz.max_choices ? quiz.max_choices > 1 : true;

  const handleAddAnswerClick = async () => {
    const newAnswer: HandleQuizAnswerData = {
      name: { cs: 'Nová odpověď', en: '', de: '' },
      kitchen_note: '',
      total_price: 1,
      tax_in_percent: 21,
      image_id: null,
      ordering: answers.length,
      max_amount: 1,
      allergens: [],
    };
    const createResponse = await createProductQuizAnswerMutation.mutateAsync({
      quizId: quiz.id,
      data: newAnswer,
    });
    if (createResponse.status === 200 && createResponse.data) {
      onQuerySuccess({
        queryClient,
        queryKeysToInvalidate: [
          ['productQuizzes', Number(id)],
          ['product', Number(id)],
        ],
      });
    } else {
      onQueryError({ errorMessage: t('QUIZ_ANSWER.CREATE_FAILURE') });
    }
  };

  return (
    <Stack direction={'column'} width="100%">
      <DialogHandleVariant
        open={addVariantDialogOpen}
        setOpen={setAddVariantDialogOpen}
        quiz={quiz}
        defaultValues={{
          name: {
            cs: quiz.name.cs || '',
            de: quiz.name.de || '',
            en: quiz.name.en || '',
          },
          multipleChoice: isMultipleChoice,
          required: quiz.min_choices > 0,
          minChoices: quiz.min_choices,
          maxChoices: quiz.max_choices ?? (isMultipleChoice ? undefined : 1),
        }}
      />
      <Stack direction={'row'} gap={2}>
        <Stack direction="column" pl={2}>
          <Typography variant="body1" fontWeight={700}>
            {getLanguageVariantByDefaultPriority(quiz.name)}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isMultipleChoice ? t('VARIANT.MULTICHOICE_LABEL') : t('VARIANT.SINGLE_CHOICE_LABEL')}
          </Typography>
        </Stack>
        <Button
          variant="text"
          sx={{ color: palette.primary.main }}
          onClick={() => setAddVariantDialogOpen(true)}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <span className="material-symbols-rounded" style={{ color: palette.primary.main }}>
              edit_square
            </span>
            {t('GLOBAL.EDIT')}
          </Stack>
        </Button>
      </Stack>
      <Table size="medium" sx={{ mt: 2, width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '1%' }}></TableCell>
            <TableCell
              sx={{
                width: { xs: '10%', xl: '25%', xxl: '15%' },
                minWidth: '250px',
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {t('QUIZ_ANSWER.NAME')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('QUIZ_ANSWER.MANDATORY')}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ width: { xs: '10%', xl: '8%', xxl: '5%' } }}>
              {t('QUIZ_ANSWER.MAX_COUNT')}
            </TableCell>
            <TableCell sx={{ width: { xs: '12%', xl: '10%', xxl: '6%' } }}>
              {t('QUIZ_ANSWER.PRICE')}
            </TableCell>
            {/* DPH */}
            <TableCell sx={{ width: '10%' }}></TableCell>
            {/* variant ingredients */}
            <TableCell sx={{ width: '1%' }}></TableCell>
            {/* remove row (x) */}
            <TableCell sx={{ width: '1%' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ '.MuiInputBase-root': { backgroundColor: palette.surface.white } }}>
          <SortableTableContents
            items={answers}
            onChange={setAnswers}
            renderItem={(item) => <VariantQuizAnswer answer={item} />}
          />
          <TableRow>
            <TableCell sx={{ borderBottom: 'none' }}></TableCell>
            <TableCell sx={{ borderBottom: 'none', color: palette.primary.main }}>
              <TextButtonWithIcon
                iconName={'add'}
                label={t('GLOBAL.ADD')}
                onClick={handleAddAnswerClick}
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Stack>
  );
};
